import { type Dispatch, type SetStateAction, useEffect } from 'react';
import type { FlatfinderPerspective } from 'realitiq-utils/src/flatfinder';

export function useCancelAssignmentOutsideAvailableAreas({
  assigningEstateId,
  perspective,
  setAssigningEstateId,
}: {
  assigningEstateId: string | null;
  perspective?: FlatfinderPerspective;
  setAssigningEstateId: Dispatch<SetStateAction<string | null>>;
}) {
  useEffect(() => {
    if (!assigningEstateId) return;

    function cancelOutsideAvailableArea(event: MouseEvent) {
      const target = event.target as Element;
      const areaId = target.closest('[data-assignment-area]')?.getAttribute('data-assignment-area');
      if (perspective?.regions.some((region) => region.id === areaId && !region.estateId)) return;
      setAssigningEstateId(null);
      if (target.closest('[data-assignment-surface]')) {
        event.preventDefault();
        event.stopPropagation();
      }
    }

    function cancelOnEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') setAssigningEstateId(null);
    }

    document.addEventListener('click', cancelOutsideAvailableArea, true);
    document.addEventListener('keydown', cancelOnEscape);
    return () => {
      document.removeEventListener('click', cancelOutsideAvailableArea, true);
      document.removeEventListener('keydown', cancelOnEscape);
    };
  }, [assigningEstateId, perspective, setAssigningEstateId]);
}

export function useFlatfinderHistoryShortcuts({
  blocked,
  onRedo,
  onUndo,
}: {
  blocked: boolean;
  onRedo: () => void;
  onUndo: () => void;
}) {
  useEffect(() => {
    function handleHistoryShortcut(event: KeyboardEvent) {
      if (blocked || !(event.metaKey || event.ctrlKey) || event.altKey) return;
      const target = event.target;
      if (
        target instanceof HTMLElement &&
        (target.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName))
      ) {
        return;
      }

      if (event.key.toLowerCase() === 'z') {
        event.preventDefault();
        if (event.shiftKey) onRedo();
        else onUndo();
      }
      if (event.key.toLowerCase() === 'y') {
        event.preventDefault();
        onRedo();
      }
    }

    window.addEventListener('keydown', handleHistoryShortcut);
    return () => window.removeEventListener('keydown', handleHistoryShortcut);
  }, [blocked, onRedo, onUndo]);
}
